import { Box, Button, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  PublicBidStatus,
  PublicBidStatusTransitionTypeDto,
  type BuyerPublicBidDetailDto,
} from "../../../../autogen/bff-api";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";
import { latestTransition } from "./bidActivity";
import type { BidEvaluationSummary } from "./bidEvaluationSummary";
import { RejectBidButton } from "./BidRejectionActionCard";

interface Props {
  detail: BuyerPublicBidDetailDto;
  summary: BidEvaluationSummary;
  onMarkWinner: () => void;
}

export const BidActionsCard = ({ detail, summary, onMarkWinner }: Props) => {
  if (detail.bid.status === PublicBidStatus.Rejected) {
    return null;
  }
  if (detail.bid.status === PublicBidStatus.Awarded) {
    return <AwardedBanner detail={detail} />;
  }
  return <ActiveActions detail={detail} summary={summary} onMarkWinner={onMarkWinner} />;
};

const AwardedBanner = ({ detail }: { detail: BuyerPublicBidDetailDto }) => {
  const { t } = useTranslation();
  const displayDateTime = useDisplayDateWithTime();
  const awardedAt = latestTransition(
    detail.bid.statusHistory,
    PublicBidStatusTransitionTypeDto.Awarded,
  )?.at;
  return (
    <Box
      bg="green.50"
      border="1px solid"
      borderColor="green.200"
      rounded="xl"
      px="5"
      py="4"
      color="green.800"
    >
      <Stack spacing="0.5">
        <Text fontWeight="semibold">{t("Selected as winner")}</Text>
        {awardedAt && (
          <Text fontSize="sm" opacity={0.85}>
            {displayDateTime(awardedAt)}
          </Text>
        )}
      </Stack>
    </Box>
  );
};

const ActiveActions = ({
  detail,
  summary,
  onMarkWinner,
}: {
  detail: BuyerPublicBidDetailDto;
  summary: BidEvaluationSummary;
  onMarkWinner: () => void;
}) => {
  const { t } = useTranslation();
  const showRejectButton = detail.bid.status === PublicBidStatus.Submitted;
  const showWinnerButton = summary.state === "evaluated" && summary.rank === 1;
  if (!showRejectButton && !showWinnerButton) {
    return null;
  }

  return (
    <Box
      bg="smBackground"
      border="1px solid"
      borderColor="smBorder"
      rounded="xl"
      px="5"
      py="4"
    >
      <Text
        fontSize="xs"
        fontWeight="bold"
        letterSpacing="wider"
        textTransform="uppercase"
        color="smMuted"
        mb="3"
      >
        {t("Actions")}
      </Text>
      <Stack spacing="2">
        {showRejectButton && <RejectBidButton detail={detail} />}
        {showWinnerButton && (
          <Button colorScheme="teal" onClick={onMarkWinner} width="full">
            {t("Mark as winner")}
          </Button>
        )}
      </Stack>
    </Box>
  );
};
